import { GridColDef, GridCellParams } from '@mui/x-data-grid'
import { t } from 'i18next'
import { NavLink, useNavigate } from 'react-router-dom'
import DeleteButton from '../components/common/DeleteButton'
import LaunchIcon from '@mui/icons-material/Launch'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import TagPriority from '../components/common/TagPriority'
import StatusTag from '../components/common/TagStatus'
import ProjecTypeTag from '../components/common/TagProjectTypes'
import { useSelector } from 'react-redux'
import { UserRole } from '../types/keycloak'
import { hasRole } from '../utils/checkRole'
import { selectUsername } from '../redux/keycloak/keycloakSelectors'
import { formatTitle } from '../utils/formatTitle'
import { selectSpecifications } from '../redux/specification/specificationSelectors'
import { formatCurrency } from '../utils/formatNumbers'
import i18n from '../i18n'
import { renderDateChip, renderBudgetChip, numericColumn } from '../utils/dataGridStyles'

const useColumnsAction = (
  handleDeleteClick?: (actionId: string) => void,
  disableActions?: boolean
): GridColDef[] => {
  disableActions = disableActions ?? false
  const navigate = useNavigate()
  const roles = useSelector((state: any) => state.auth.roles)
  const username = useSelector(selectUsername)

  const currency = useSelector(selectSpecifications)[0]?.Currency

  return [
    {
      field: 'ActionName',
      headerName: t('task_name'),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <NavLink
          className="line-clamp-2 overflow-hidden text-ellipsis"
          to={`/actiond/${params.id}`}
        >
          <strong style={{ color: '#132743' }}>
            {formatTitle(params.value as string)}
          </strong>
        </NavLink>
      ),
    },
    {
      field: 'Description',
      headerName: t('Description'),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.row.Description}
        </span>
      ),
    },
    {
      field: 'DateDebut',
      headerName: t('Start Date'),
      minWidth: 130,
      flex: 1,
      renderCell: (params: GridCellParams) => renderDateChip(params.row.DateDebut, i18n.language),
    },
    {
      field: 'DateFin',
      headerName: t('End Date'),
      minWidth: 130,
      flex: 1,
      renderCell: (params: GridCellParams) => renderDateChip(params.row.DateFin, i18n.language),
    },
    {
      field: 'Priority',
      headerName: t('Priority'),
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          <TagPriority priority={params.row.Priority} />
        </span>
      ),
    },
    {
      field: 'TypeAction',
      headerName: t('task_type'),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          <ProjecTypeTag value={params.row.TypeAction} />
        </span>
      ),
    },
    {
      field: 'Status',
      headerName: t('Status'),
      minWidth: 120,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          <StatusTag value={params.row.Status} />
        </span>
      ),
    },
    {
      field: 'Cout',
      headerName: t('cost'),
      flex: 1,
      ...numericColumn(),
      renderCell: (params: GridCellParams) => renderBudgetChip(params.row.Cout ?? 0, currency, i18n.language),
    },
    {
      field: 'CoutTotal',
      headerName: t('total_cost'),
      flex: 1,
      ...numericColumn(),
      renderCell: (params: GridCellParams) => renderBudgetChip(params.row.CoutTotal ?? 0, currency, i18n.language),
    },
    {
      field: 'MissionName',
      headerName: t('Mission Name'),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.row.MissionName}
        </span>
      ),
    },
    ...(disableActions === false
      ? [
          {
            field: 'Action',
            headerName: t('Action'),
            minWidth: 100,
            flex: 0.5,
            renderCell: (params: GridCellParams) => (
              <>
                <LaunchIcon
                  className="iconlaunch"
                  onClick={() => navigate(`/actiond/${params.id}`)}
                />

                {username?.toLocaleLowerCase() ===
                  params?.row?.CreatedBy?.toLocaleLowerCase() && (
                  <DriveFileRenameOutlineOutlinedIcon
                    className="iconEdit"
                    onClick={() => navigate(`/action/${params.id}`)}
                  />
                )}

                {(hasRole(roles, UserRole.RSSI) ||
                  hasRole(roles, UserRole.SSI_TEAM) ||
                  hasRole(roles, UserRole.ADMIN)) &&
                  handleDeleteClick && (
                    <DeleteButton
                      onDelete={() => handleDeleteClick(params.id as string)}
                      item={{
                        entityType: t('tasks', { defaultValue: 'Task' }),
                        name: params.row.ActionName
                          ? formatTitle(params.row.ActionName as string)
                          : undefined,
                        details: [
                          {
                            label: t('deleteConfirm.fields.status'),
                            value: params.row.Status || '—',
                          },
                          {
                            label: t('deleteConfirm.fields.priority'),
                            value: String(params.row.Priority ?? '—'),
                          },
                          {
                            label: t('deleteConfirm.fields.mission'),
                            value: params.row.MissionName || '—',
                          },
                        ],
                      }}
                    />
                  )}
              </>
            ),
          },
        ]
      : []),
  ]
}

export default useColumnsAction
